import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  BookOpen, 
  Clock, 
  Link as LinkIcon,
  Star,
  Grid,
  List,
  Presentation
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import { lessonService } from '../../services/lessonService';
import { Lesson } from '../../types';
import toast from 'react-hot-toast';
import { getGrades, getSubjects } from '../../utils/lessons';
import { ExportDropdown } from './ExportDropdown';

const LessonCard: React.FC<{ 
  lesson: Lesson; 
  onLink: (lessonId: string) => void;
  isLinked?: boolean;
}> = ({ lesson, onLink, isLinked = false }) => {
  const { t } = useTranslation();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card hover:shadow-lg transition-all duration-200"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {lesson.metadata.title}
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            {lesson.metadata.subject} • {t('lesson.grade')} {lesson.metadata.grade}
            {lesson.metadata.subconstruct && ` • ${lesson.metadata.subconstruct}`}
            {lesson.className && ` • ${lesson.className}`}
          </p>
          <p className="text-gray-700 line-clamp-2 mb-4">
            {lesson.objectives[0] || t('lessonList.noDescription')}
          </p>
        </div>
        {lesson.metadata.isAdminLesson && (
          <div className="ml-4 flex items-center">
            <Star className="w-4 h-4 text-yellow-400 fill-current" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center space-x-4">
            <span className="flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {lesson.metadata.duration_minutes} {t('lessonList.min')}
            </span>
            <span className="flex items-center">
              <BookOpen className="w-3 h-3 mr-1" />
              {lesson.timeline.length} {t('lessonList.steps')}
            </span>
          </div>
        </div>
        
        <div className="flex items-center justify-end">
          <Link
            to={`/lessons/${lesson.metadata.lesson_id}/slides`}
            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors mr-2"
            title={t('lessonList.openSlides')}
          >
            <Presentation className="w-4 h-4" />
          </Link>
          <ExportDropdown
            lesson={lesson}
            size="sm"
            variant="ghost"
            className="text-sm"
          />
          {isLinked ? (
            <span className="text-sm text-green-600 font-medium">
              {t('lessonLibrary.alreadyLinked')}
            </span>
          ) : (
            <button
              onClick={() => onLink(lesson.metadata.lesson_id)}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <LinkIcon className="w-4 h-4" />
              <span>{t('lessonLibrary.linkLesson')}</span>
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const LessonLibrary: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [adminLessons, setAdminLessons] = useState<Lesson[]>([]);
  const [teacherLessons, setTeacherLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [selectedGrade, setSelectedGrade] = useState('All');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const subjects = getSubjects(t);
  
  const grades = getGrades(t);

  useEffect(() => {
    const fetchLessons = async () => {
      try {
        setLoading(true);
        const [admin, teacher] = await Promise.all([
          lessonService.getAdminLessons(),
          lessonService.getAllLessons(user?.id)
        ]);
        setAdminLessons(admin);
        setTeacherLessons(teacher);
      } catch (error) {
        console.error('Error fetching lessons:', error);
        toast.error(t('lessonLibrary.fetchError'));
      } finally {
        setLoading(false);
      }
    };

    fetchLessons();
  }, [user?.id, t]);

  const handleLinkLesson = async (lessonId: string) => {
    if (!user?.id) {
      toast.error(t('lessonLibrary.loginRequired'));
      return;
    }

    try {
      await lessonService.linkLessonToTeacher(lessonId, user.id);
      toast.success(t('lessonLibrary.linkSuccess'));
      
      // Refresh teacher lessons
      const updatedTeacherLessons = await lessonService.getAllLessons(user.id);
      setTeacherLessons(updatedTeacherLessons);
    } catch (error) {
      console.error('Error linking lesson:', error);
      toast.error(t('lessonLibrary.linkError'));
    }
  };

  const filteredLessons = adminLessons.filter(lesson => {
        const matchesSearch = lesson.metadata.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lesson.metadata.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lesson.className && lesson.className.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesSubject = selectedSubject === 'All' || lesson.metadata.subject === selectedSubject;
    const matchesGrade = selectedGrade === 'All' || lesson.metadata.grade.toString() === selectedGrade;
    
    return matchesSearch && matchesSubject && matchesGrade;
  });

  const isLessonLinked = (lessonId: string) => {
    return teacherLessons.some(lesson => lesson.metadata.linkedFrom === lessonId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('lessonLibrary.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('lessonLibrary.title')}</h1>
          <p className="text-gray-600">{t('lessonLibrary.description')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Search */}
          <div className="flex-1 min-w-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('lessonLibrary.searchLessons')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10 w-full"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center space-x-2">
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="input-field flex-1"
              >
                <option value="All">{t('lessonList.all')}</option>
                {subjects.map(subject => (
                  <option key={subject.value} value={subject.value}>{subject.label}</option>
                ))}
              </select>
            </div>

            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="input-field flex-1"
            >
              <option value="All">{t('lessonList.all')}</option>
              {grades.map(grade => (
                <option key={grade.value} value={grade.value}>{t('lesson.grade')} {grade.label}</option>
              ))}
            </select>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {t('lessonLibrary.results', { count: filteredLessons.length })}
        </p>
      </div>

      {/* Lessons Grid/List */}
      {filteredLessons.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('lessonLibrary.noLessons')}
          </h3>
          <p className="text-gray-600">
            {t('lessonLibrary.noLessonsDescription')}
          </p>
        </div>
      ) : (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
          : 'space-y-4'
        }>
          {filteredLessons.map((lesson) => (
            <LessonCard
              key={lesson.metadata.lesson_id}
              lesson={lesson}
              onLink={handleLinkLesson}
              isLinked={isLessonLinked(lesson.metadata.lesson_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
